function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export type BrandLogoVariant =
  'search_avatar' | 'chip' | 'modal_icon' | 'hero_logo_light' | 'hero_logo_dark';

export function normalizeBrandDomain(domain: string | null | undefined): string | null {
  const raw = asNonEmptyString(domain);
  if (!raw) return null;

  const withoutProtocol = raw.replace(/^https?:\/\//i, '');
  const withoutPath = withoutProtocol.split(/[/?#]/, 1)[0] ?? '';
  const normalized = withoutPath.replace(/^www\./i, '').toLowerCase();

  if (!normalized || !normalized.includes('.')) return null;
  if (!/^[a-z0-9.-]+$/.test(normalized)) return null;
  if (normalized.startsWith('.') || normalized.endsWith('.')) return null;

  return normalized;
}

export function getBrandfetchClientId(): string | null {
  return asNonEmptyString(process.env.NEXT_PUBLIC_BRANDFETCH_CLIENT_ID);
}

export function getBrandLogoUrlFromDomain(
  domain: string | null | undefined,
  variant: BrandLogoVariant = 'chip',
  clientId: string | null = getBrandfetchClientId(),
): string | null {
  const normalizedDomain = normalizeBrandDomain(domain);
  if (!normalizedDomain || !clientId) return null;

  const path = buildVariantPath(variant);
  return `https://cdn.brandfetch.io/domain/${normalizedDomain}/${path}?c=${encodeURIComponent(
    clientId,
  )}`;
}

/**
 * Brand search returns icons signed with a temporary Brandfetch client id that later
 * fails with 410 client_id_expired. Re-sign every Brandfetch CDN address with our own
 * id when saving and showing it, so logos saved earlier keep working too.
 */
export function withBrandfetchClientId<T extends string | null | undefined>(
  url: T,
  clientId: string | null = getBrandfetchClientId(),
): T {
  if (!url || !clientId) return url;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' || parsed.hostname !== 'cdn.brandfetch.io') return url;
    parsed.searchParams.set('c', clientId);
    return parsed.toString() as T;
  } catch {
    return url;
  }
}

export function getBrandDomainFromData(brandData?: Record<string, unknown>): string | null {
  if (!brandData) return null;
  return (
    asNonEmptyString(brandData.domain) ??
    asNonEmptyString(brandData.domain_name) ??
    asNonEmptyString(brandData.website) ??
    null
  );
}

export function getBrandLogoUrlFromData(
  brandData?: Record<string, unknown>,
  variant: BrandLogoVariant = 'chip',
  clientId: string | null = getBrandfetchClientId(),
): string | null {
  return getBrandLogoUrlFromDomain(getBrandDomainFromData(brandData), variant, clientId);
}

function buildVariantPath(variant: BrandLogoVariant): string {
  switch (variant) {
    case 'search_avatar':
      return 'w/40/h/40/fallback/404/icon';
    case 'chip':
      return 'w/32/h/32/fallback/404/icon';
    case 'modal_icon':
      return 'w/128/h/128/fallback/404/icon';
    case 'hero_logo_light':
      return 'w/800/h/240/theme/light/fallback/404/logo';
    case 'hero_logo_dark':
      return 'w/800/h/240/theme/dark/fallback/404/logo';
    default:
      return 'w/32/h/32/fallback/404/icon';
  }
}
